import { Log } from '../AuxillaryService/Log';
import { $ } from '../../vendor/jquery';

/**
 * Successful Response
 *
 * Returns 5 values:
 *
 * 1. {*} data
 * 2. {Event} event
 * 3. {IDBRequest} request
 * 4. {IDBObjectStore} objectStore
 * 5. {IDBDatabase|null} db
 * @typedef {jQuery.Promise} jqpSuccessIndexedDB
 */

/**
 * Failure Response
 *
 * Returns 2 values:
 *
 * 1. {string} msg
 * 2. {Event} event
 * @typedef {jQuery.Promise} jqpFailureIndexedDB
 */

/**
 *
 * @param {*} databaseName
 * @param {*} objectStoreName
 * @class
 * @todo document this
 */
function PromisedIndexedDB(databaseName, objectStoreName) {
    this._databaseName = databaseName;
    this._objectStoreName = objectStoreName;

    //this._databaseVersion = null;
    //this._onupgradeneeded = null;

    /**
     * @type {jQuery.Deferred<IDBDatabase>|jQuery.Deferred<string, Event>|null}
     * @private
     */
    this._openDatabaseDeferred = null;

    /**
     * @type IDBFactory
     * @private
     */
    this._indexedDB =
        window.indexedDB ||
        window.webkitIndexedDB ||
        window.mozIndexedDB ||
        window.OIndexedDB ||
        window.msIndexedDB;
}

/**
 * Try to connect to IndexedDB database
 * @throws {jqpFailureIndexedDB}
 * @returns {jQuery.Promise<IDBDatabase>}
 */
PromisedIndexedDB.prototype.openDatabase = function () {
    if (this._openDatabaseDeferred === null) {
        this._openDatabaseDeferred = $.Deferred();

        var request = this._indexedDB.open(this._databaseName);

        request.onerror = function (event) {
            Log.Error('Failed to open database `' + this._databaseName + '`', null, __fileline, {
                event: event,
                message: event.target.error.message,
            });
            this._openDatabaseDeferred.reject('Failed to open database', event);
        }.bind(this);

        request.onsuccess = function (event) {
            Log.Info(
                'Connection to `' + this._databaseName + '` database successful',
                null,
                __fileline
            );
            var database = event.target.result;
            this._openDatabaseDeferred.resolve(database);
        }.bind(this);
    }

    return this._openDatabaseDeferred.promise();
};

/**
 * Try to get the `objectStore` of the IndexedDB
 * @param {string} [mode=readonly]
 * @throws {jqpFailure}
 * @returns {jQuery.Promise<IDBObjectStore, IDBDatabase>}
 */
PromisedIndexedDB.prototype.getObjectStore = function (mode) {
    mode = mode || 'readonly';

    var jqDeferred = $.Deferred();

    this.openDatabase()
        .done(
            function (db) {
                var objectStore = db
                    .transaction(this._objectStoreName, mode)
                    .objectStore(this._objectStoreName);

                jqDeferred.resolve(objectStore, db);
            }.bind(this)
        )
        .fail(function (msg, event) {
            jqDeferred.reject(msg, event);
        });

    return jqDeferred.promise();
};

/**
 * Try to get data out of the IndexedDB
 * @param {string} index
 * @param {*|IDBKeyRange} key
 * @param {IDBObjectStore} [objectStore]
 * @throws {jqpFailureIndexedDB}
 * @returns {jqpSuccessIndexedDB} `data` is an `Object`
 * containing the record's data
 */
PromisedIndexedDB.prototype.getRecord = function (index, key, objectStore) {
    var jqDeferred = $.Deferred();
    var objectStoreJqPromise;

    if (objectStore instanceof IDBObjectStore) {
        objectStoreJqPromise = $.Deferred();
        objectStoreJqPromise.resolve(objectStore, null);
    } else {
        objectStoreJqPromise = this.getObjectStore('readonly');
    }

    objectStoreJqPromise.done(function (objectStore, db) {
        var getIndex;
        var getRequest;

        if (index === null || index === 'id') {
            // get by primary key
            getRequest = objectStore.get(key);
        } else {
            getIndex = objectStore.index(index);
            getRequest = getIndex.get(key);
        }

        getRequest.onsuccess = function (event) {
            var data = event.target.result;
            jqDeferred.resolve(data, event, getRequest, objectStore, db);
        };

        getRequest.onerror = function (event) {
            jqDeferred.reject('Failed to retrieve from database', event);
        };
    });

    objectStoreJqPromise.fail(function (msg, event) {
        jqDeferred.reject(msg, event);
    });

    return jqDeferred.promise();
};

/**
 * Try to put data into the IndexedDB
 * @param {*} data
 * @param {IDBObjectStore} [objectStore]
 * @throws {jqpFailureIndexedDB}
 * @returns {jqpSuccessIndexedDB} `data` is an `number` which is
 * the ID of the new record
 */
PromisedIndexedDB.prototype.putRecord = function (data, objectStore) {
    var jqDeferred = $.Deferred();
    var objectStoreJqPromise;

    if (objectStore instanceof IDBObjectStore) {
        objectStoreJqPromise = $.Deferred();
        objectStoreJqPromise.resolve(objectStore, null);
    } else {
        objectStoreJqPromise = this.getObjectStore('readwrite');
    }

    objectStoreJqPromise.done(function (objectStore, db) {
        var putRequest = objectStore.put(data);

        putRequest.onsuccess = function (event) {
            var record = event.target.result;
            jqDeferred.resolve(record, event, putRequest, objectStore, db);
        };

        putRequest.onerror = function (event) {
            jqDeferred.reject('Failed to save to database', event);
        };
    });

    objectStoreJqPromise.fail(function (msg, event) {
        jqDeferred.reject(msg, event);
    });

    return jqDeferred.promise();
};

/**
 * @param {string} index
 * @param {*|IDBKeyRange} key
 * @param {IDBObjectStore} [objectStore]
 * @throws {jqpFailureIndexedDB}
 * @returns {jqpSuccessIndexedDB} `data` is an `Event`
 */
PromisedIndexedDB.prototype.deleteRecord = function (index, key, objectStore) {
    var jqDeferred = $.Deferred();
    var objectStoreJqPromise;

    if (objectStore instanceof IDBObjectStore) {
        objectStoreJqPromise = $.Deferred();
        objectStoreJqPromise.resolve(objectStore, null);
    } else {
        objectStoreJqPromise = this.getObjectStore('readwrite');
    }

    objectStoreJqPromise.done(function (objectStore, db) {
        var deleteIndex = objectStore.index(index);
        var deleteRange = IDBKeyRange.only(key);
        var deleteRequest = deleteIndex.openKeyCursor(deleteRange);

        deleteRequest.onsuccess = function (event) {
            var cursor = event.target.result;

            if (cursor) {
                // Delete record
                objectStore.delete(cursor.primaryKey);
                cursor.continue();
            } else {
                jqDeferred.resolve(event, deleteRequest, objectStore, db);
            }
        };

        deleteRequest.onerror = function (event) {
            jqDeferred.reject('Failed to delete from database', event);
        };
    });

    objectStoreJqPromise.fail(function (msg, event) {
        jqDeferred.reject(msg, event);
    });

    return jqDeferred.promise();
};

/**
 * @param {string} index
 * @param {IDBObjectStore} [objectStore]
 * @throws {jqpFailureIndexedDB}
 * @returns {PromiseLike<*>} `data` is an `Object`
 * containing the keys
 */
PromisedIndexedDB.prototype.getKeys = function (index, objectStore) {
    var jqDeferred = $.Deferred();
    var objectStoreJqPromise;

    if (objectStore instanceof IDBObjectStore) {
        objectStoreJqPromise = $.Deferred();
        objectStoreJqPromise.resolve(objectStore, null);
    } else {
        objectStoreJqPromise = this.getObjectStore('readonly');
    }

    objectStoreJqPromise.done(function (objectStore, db) {
        var keyIndex = objectStore.index(index);
        var cursorRequest = keyIndex.openKeyCursor();

        var keys = [];

        cursorRequest.onsuccess = function (event) {
            var cursor = event.target.result;
            if (cursor) {
                // Add entry
                keys.push({
                    key: cursor.key,
                    primaryKey: cursor.primaryKey,
                });

                cursor.continue();
            } else {
                // All entries added
                jqDeferred.resolve(keys, event, cursorRequest, objectStore, db);
            }
        };

        cursorRequest.onerror = function (event) {
            jqDeferred.reject('Failed to retrieve keys', event);
        };
    });

    objectStoreJqPromise.fail(function (msg, event) {
        jqDeferred.reject(msg, event);
    });

    return jqDeferred.promise();
};

export { PromisedIndexedDB };
